import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent, Button, type ModalProps } from '@heroui/react';
import type { Variants } from 'framer-motion';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import useClientStore from '@/store/useClientStore';
import { isUsa } from '@/lib/country';

/* ---------------------------- Media Query Hook --------------------------- */
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);
  return matches;
};

interface ModalSlideFilterProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

/* ------------------------------ Motion config --------------------------- */
// `Variants`: framer-motion 12.34 tipa `ease` como `Easing`, no `string`.
const motionVariants: Variants = {
  enter: { x: 0, transition: { duration: 0.28, ease: 'easeOut' } },
  exit: { x: -520, transition: { duration: 0.34, ease: 'easeOut' } },
};

/* ---------------------------- ClassNames (memo) -------------------------- */
// El `z-[9999]` va en el WRAPPER, no sólo en el `base`. HeroUI envuelve el
// modal en un `fixed inset-0 z-50` propio, y ese wrapper es el que crea el
// stacking context: el 9999 del hijo nunca se compara contra el resto de la
// página, sólo el 50 del wrapper. Los navbars del builder son `zIndex: 100`,
// así que 100 > 50 y el header quedaba pintado ENCIMA del panel de filtros,
// tapándole el título y el botón "Limpiar filtros".
const modalClassNames = {
  base: 'justify-start m-0 p-0 h-dvh max-h-full w-[86vw] max-w-[320px]',
  wrapper: 'items-start justify-start !w-[86vw] max-w-[320px] !z-[9999]',
  backdrop: '!z-[9998]',
  body: 'p-0',
  closeButton:
    'z-50 top-3 right-3 left-auto m-0 text-xl dark:bg-dark-card/80 w-8 h-8 shadow-sm flex items-center justify-center dark:border-dark-border',
} as const;

const ModalSlideFilter = React.memo(
  ({ children, isOpen, onClose }: ModalSlideFilterProps) => {
    // 'Aplicar filtros' en es y pt (como siempre); 'Apply filters' en inglés.
    // 'Cerrar' solo cambia en EE.UU. (en pt la clave dice 'Fechar').
    const { t } = useTranslation();
    const enUsa = isUsa(useClientStore((s) => s.client?.country));
    const [isMounted, setIsMounted] = useState(false);
    const isMd = useMediaQuery('(min-width: 768px)');
    useEffect(() => setIsMounted(true), []);
    if (!isMounted) return null;

    // Desktop = panel deslizante fijo (no modal)
    const desktopClassName = `h-screen bg-white dark:bg-dark-card shadow-md transition-all duration-300 ${
      isOpen ? 'translate-x-0 w-64' : '-translate-x-full w-0'
    } overflow-hidden`;

    if (isMd) {
      return (
        <div className={desktopClassName}>
          <div className="flex flex-col h-full">
            <div className="flex-1 min-h-0">{children}</div>
            <div className="shrink-0 w-full p-3 bg-white dark:bg-dark-card border-t border-gray-200 dark:border-dark-border">
              <div className="flex gap-2">
                <Button size="md" className="flex-1 rounded-lg" variant="flat" onClick={onClose}>
                  {enUsa ? t('common.close') : 'Cerrar'}
                </Button>
                <Button size="md" color="primary" className="flex-1 rounded-lg" onClick={onClose}>
                  {t('vehicles.filters.applyFilters')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Mobile = modal con slide-from-left
    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={onClose}
        classNames={{
          ...modalClassNames,
          body: 'p-0 flex flex-col h-full',
        }}
        motionProps={{ variants: motionVariants }}
        radius="none"
        scrollBehavior="inside"
        // 'left' no está en la unión de `placement` de HeroUI (nunca lo estuvo):
        // el modal no le aplica ninguna clase por este valor y la posición a la
        // izquierda la dan `modalClassNames`. Se conserva el valor tal cual para
        // no cambiar el runtime: sin la prop caería al default 'auto', que SÍ
        // agrega clases de alineación.
        placement={'left' as unknown as ModalProps['placement']}
      >
        <ModalContent>
          <ModalBody>
            {/* El scroll lo maneja el panel de filtros, que ya es una columna
                flex con su propia zona scrolleable. Acá sólo se le da el alto
                (`flex-1 min-h-0`); poner otro `overflow-y-auto` encima creaba
                el scroll anidado que hacía incómodo llegar a los últimos
                filtros. El footer es `shrink-0` para no ceder alto nunca. */}
            <div className="flex flex-col h-full">
              <div className="flex-1 min-h-0">{children}</div>
              <div className="shrink-0 w-full p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-white dark:bg-dark-card border-t border-gray-200 dark:border-dark-border">
                <Button color="primary" className="w-full" size="lg" onClick={onClose}>
                  {t('vehicles.filters.applyFilters')}
                </Button>
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }
);

ModalSlideFilter.displayName = 'ModalSlideFilter';
export default ModalSlideFilter;
